import React, { useState, useRef, useEffect } from 'react';
import SouthernLivingModal from '../modal/SouthernLivingModal';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const testimonials = [
  {
    id: 1,
    quotePrimary: 'Propane is a reliable source of energy that you, as the homeowner, control and can be used in many ways. It is versatile and has great longevity.',
    name: 'Allan Pettit',
    title: 'Project Manager',
    avatar: '/wp-content/themes/wlion-2021/assets/images/Southern_Living_prof_2.jpg',
    titleModal: 'Why the Pros Rely on Propane',
    qa: [
      {
        question: 'Overall, how was your experience working with propane in the 2025 Idea House?',
        answer: 'Our experience with propane was great. In the market that we serve, over half of our projects do not have access to a natural gas utility and therefore we work with propane frequently.'
      },
      {
        question: `The 2025 Southern Living Idea House has a propane standby generator. How important is resilient, reliable backup power in today's residential construction market? `,
        answer: 'A backup generator has become a standard client expectation. Propane is always our go-to as the fuel source because it is the only fuel source that is stable and, when stored correctly, doesn’t expire.'
      },
      {
        question: 'Were there any unexpected benefits of using propane?',
        answer: 'It wasn’t unexpected, but because propane is able to be used in hot water production and emergency or backup heating, it reduced the electrical demands of the house, and we were able to reduce the size of the generator by 50%. Because of the distance of the house from the road, propane tank burial and running lines to the house was less costly than natural gas service. This also meant that the area of soil disturbance was smaller, since we didn’t excavate a long trench from the road.'
      },
      {
        question: 'What role did propane play in creating comfort and peace of mind for the future homeowners?',
        answer: 'One notable difference between propane and natural gas is that once the product has been delivered, you own and control it. With natural gas, there are situations that can cause service interruption. There is energy independence that exists with propane that you don’t have with natural gas, which translates into peace of mind once it’s coupled with a propane-powered backup or standby generator. Also, I’m not sure everyone knows this, but additional comfort comes from the fact that propane doesn’t go bad or expire. Most, if not all, other fuels that can be stored on site have a shelf life and will go bad if not used. That is not the case with propane.'
      },
      {
        question: 'Would you recommend propane to other builders or homeowners?',
        answer: 'Yes — especially for its versatility and reliability.'
      }
    ],
    cta: {
      label: 'Learn More',
      link: '/for-my-home',
    },
  },
  {
    id: 2,
    quotePrimary: 'For a home designed to balance luxury, comfort, and resilience, propane helped us bring that vision to life.',
    name: 'Julie Kline Dixon',
    title: 'Architect',
    avatar: '/wp-content/themes/wlion-2021/assets/images/Southern_Living_prof_1.jpg',
    titleModal: 'Why the Pros Rely on Propane',
    qa: [
      {
        question: 'How did the use of propane contribute to the function or flexibility of the 2025 Southern Living Idea House, especially given its location?',
        answer: 'Given the location of the 2025 Southern Living Idea House — set near natural streams and situated away from municipal natural gas lines — propane offered a perfect energy solution that gave us both flexibility and performance.'
      },
      {
        question: 'Were there any unexpected benefits of using propane?',
        answer: 'Propane allowed us to incorporate high-efficiency heating, a gourmet-style kitchen with gas cooking, an on-demand water heater, and even backup power options, all without being tied to the limitations of utility infrastructure. That flexibility was key, especially in a region like central Virginia where access to natural gas isn’t guaranteed in more scenic or tucked-away parcels of land.'
      },
      {
        question: `The 2025 Southern Living Idea House has a propane standby generator. How important is resilient, reliable backup power in today's residential construction market?`,
        answer: 'Anyone who has lived down a country lane can tell you that power outages can last for days, and even weeks. Generators in areas like this one are used regularly and save homeowners from having to deal with a total collapse of their lives. That piece of mind is worth every penny.'
      },
      {
        question: 'For someone looking to build or renovate a home, why should they consider propane as part of the plan?',
        answer: `Propane also aligns well with our sustainability goals: it's a clean-burning fuel with a lower carbon footprint than many traditional energy sources, and it's easy to store safely on site. For a home designed to balance luxury, comfort, and resilience, propane helped us bring that vision to life.`
      },
      {
        question: 'Would you recommend propane to other builders or homeowners?',
        answer: 'Without question.'
      }
    ],
    cta: {
      label: 'Learn More',
      link: '/for-my-home',
    },
  }
];

const SouthernLivingTestimonials = () => {
  const el = useRef(null);
  const [activeModalIndex, setActiveModalIndex] = useState(null);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [scrollAnimationDone, setScrollAnimationDone] = useState(false);

  const testimonialCardsWrapperRef = useRef(null);
  const frontCardRef = useRef(null);
  const backCardRef = useRef(null);
  const scrollingRef = useRef(false);
  const clickedRef = useRef(false);
  useEffect(() => { clickedRef.current = hasBeenClicked; }, [hasBeenClicked]);
  const scrollEndTO = useRef(0);

  const getBackgroundColor = (index, total) => {
    const stepsFromBottom = total - 1 - index;
    const base = 255, step = 11;
    const val = Math.max(222, base - step * stepsFromBottom);
    return `rgb(${val}, ${val}, ${val})`;
  };

  const handleOpenModal = (index) => setActiveModalIndex(index);
  const handleCloseModal = () => setActiveModalIndex(null);

  useEffect(() => {
    const BACK_Y = 600;
    const FRONT_Y = 400;
    const PAD_START = BACK_Y;
    const PAD_END = 64;
    const DESKTOP_BP = 900;

    const handleResize = () => {
      const mobile = window.innerWidth < DESKTOP_BP;
      if (mobile === isMobile) return;

      setIsMobile(mobile);

      if (!scrollAnimationDone) {
        setHasBeenClicked(false);
        gsap.set([frontCardRef.current, backCardRef.current], { y: 0 });
        gsap.set(testimonialCardsWrapperRef.current, { paddingBottom: "0px" });
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    const onScrollStart = () => {
      scrollingRef.current = true;
      clearTimeout(scrollEndTO.current);
    };
    const onScrollEnd = () => {
      clearTimeout(scrollEndTO.current);
      scrollEndTO.current = setTimeout(() => (scrollingRef.current = false), 20);
    };
    ScrollTrigger.addEventListener("scrollStart", onScrollStart);
    ScrollTrigger.addEventListener("scrollEnd", onScrollEnd);

    const ctx = gsap.context(() => {
      if (window.innerWidth >= DESKTOP_BP) {
        const section = el.current;
        const back = backCardRef.current;
        const front = frontCardRef.current;
        const wrap = testimonialCardsWrapperRef.current;

        gsap.set(back, { y: BACK_Y, filter: "brightness(1)" });
        gsap.set(front, { y: FRONT_Y });
        gsap.set(wrap, { paddingBottom: PAD_START });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: "top 92%",
            end: "top 75%",
            scrub: 0.6,
            invalidateOnRefresh: true,
            onUpdate: (st) => {
              const p = st.progress;

              setScrollAnimationDone(p >= 0.18);
              if (p <= 0.02 && clickedRef.current) {
                setHasBeenClicked(false);
              }
            },
          },
          defaults: { ease: "power3.out" }
        });

        tl.to(back, { y: 0, duration: 1.0 }, 0)
          .to(front, { y: 0, duration: 1.0 }, 0.25)
          .to(wrap, { paddingBottom: PAD_END, ease: "none", duration: 1.0 }, 0);

        section.querySelectorAll("img").forEach((img) => {
          if (!img.complete) img.addEventListener("load", ScrollTrigger.refresh, { once: true });
        });
        requestAnimationFrame(() => ScrollTrigger.refresh());
      }
    }, el);

    return () => {
      window.removeEventListener("resize", handleResize);
      ScrollTrigger.removeEventListener("scrollStart", onScrollStart);
      ScrollTrigger.removeEventListener("scrollEnd", onScrollEnd);
      clearTimeout(scrollEndTO.current);
      ctx.revert();
    };
  }, []);


  const handleBackCardClick = () => {
    if (scrollingRef.current) return;
    if (!hasBeenClicked) {
      const yVal = isMobile ? "9rem" : "10.5rem";
      const tl = gsap.timeline();
      tl.to(testimonialCardsWrapperRef.current, {
        paddingBottom: yVal,
        duration: 0.5,
        ease: "power2.out",
      }, 0)
        .to(frontCardRef.current, {
          y: yVal,
          duration: 0.5,
          ease: "power2.out",
        }, 0);

      setHasBeenClicked(true);
    }
  };

  const handleBackCardHover = (hovering) => {
    if (isMobile) return;
    if (scrollingRef.current) return;
    if (hasBeenClicked) return;
    if (!scrollAnimationDone) return;

    const y = "10.5rem";

    gsap.to(testimonialCardsWrapperRef.current, {
      paddingBottom: hovering ? y : 0,
      duration: 0.5,
      ease: "power2.out",
      overwrite: "auto",
    });

    gsap.to(frontCardRef.current, {
      y: hovering ? y : 0,
      duration: 0.5,
      ease: "power2.out",
      overwrite: "auto",
    });
  };

  return (
    <section ref={el} className="testimonials-wrapper">
      <div className="testimonials-section">
        <div className="testimonials-section__intro">
          <h2 className="testimonials-section__title">Why the Pros Rely on Propane</h2>
          <p className="testimonials-section__introtext">
            Hear from <strong>Allan Pettit</strong> and <strong>Julie Kline Dixon, Project Manager and Architect for the 2025 Southern Living Idea House</strong>, about why the many benefits of propane make it the ideal energy source.
          </p>
        </div>

        <div className="testimonial-cards" ref={testimonialCardsWrapperRef}>
          {testimonials.map((testimonial, index) => {
            const isBackCard = index === 0;
            const cardRef = index === 1 ? frontCardRef : backCardRef;

            return (
              <div
                ref={cardRef}
                key={testimonial.id}
                className="testimonial-card"
                style={{
                  backgroundColor: getBackgroundColor(index, testimonials.length),
                  zIndex: index === 1 ? 2 : 1,
                  cursor: isBackCard && !hasBeenClicked ? 'pointer' : 'auto',
                }}
                onClick={isBackCard ? handleBackCardClick : undefined}
                onMouseEnter={isBackCard ? () => handleBackCardHover(true) : undefined}
                onMouseLeave={isBackCard ? () => handleBackCardHover(false) : undefined}
              >
                <div className="testimonial-card__quote">
                  <div className="testimonial-card__footer">
                    <img src={testimonial.avatar} alt={testimonial.name} className="testimonial-card__avatar" loading="lazy" />
                    <div>
                      <div className="testimonial-card__name">{testimonial.name}</div>
                      <div className="testimonial-card__title">{testimonial.title}</div>
                    </div>
                  </div>
                  <p>“{testimonial.quotePrimary}”</p>
                  <button
                    title={`Click to learn more about ${testimonial.title}'s testimonial`}
                    className="testimonial-card__button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenModal(index);
                    }}
                  >
                    READ MORE
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <SouthernLivingModal
          isOpen={activeModalIndex !== null}
          onClose={handleCloseModal}
          title={testimonials[activeModalIndex]?.titleModal}
          description={testimonials[activeModalIndex]?.description}
          images={testimonials[activeModalIndex]?.images}
          cta={testimonials[activeModalIndex]?.cta}
          qa={testimonials[activeModalIndex]?.qa}
          name={testimonials[activeModalIndex]?.name}
          titleLine={testimonials[activeModalIndex]?.title}
          avatar={testimonials[activeModalIndex]?.avatar}
          variant="testimonial"
        />
      </div>
    </section>
  );
};

export default SouthernLivingTestimonials;